/**
 * Menu items use `icon` (API) or `iconClass`; values may be `bi-building` without the base `bi` class.
 * Returns a class string suitable for Bootstrap Icons on `<i>`.
 */
export const normalizeBootstrapIconClass = (raw) => {
  if (raw == null) return "";
  const t = String(raw).trim();
  if (!t) return "";
  if (/\bbi\s+bi-[\w-]+/.test(t)) return t;
  if (/^bi-[\w-]+$/.test(t)) return `bi ${t}`;
  if (/^bi$/i.test(t)) return "bi bi-circle";
  return `bi bi-${t.replace(/^bi-?/i, "")}`;
};

export const CUSTOMER_MENU_ICON = "bi bi-person-vcard";
export const SUPPLIER_MENU_ICON = "bi bi-shop";

/** Icons for menu rows the API leaves blank. Service Request uses the same box icon as Inbound. */
export const fallbackMenuIcon = (item) => {
  const label = `${item?.title || ""} ${item?.content || ""}`
    .toLowerCase()
    .replace(/[_-]+/g, " ");
  if (label.includes("dashboard")) return "bi bi-speedometer2";
  if (label.includes("service") && label.includes("req")) return "bi bi-box";
  if (label.includes("import") && label.includes("cycle")) return "bi bi-arrow-repeat";
  if (label.includes("request") && label.includes("cycle")) return "bi bi-arrow-repeat";
  if (label.includes("appointment")) return "bi bi-calendar-check";
  if (label.includes("adjustment")) return "bi bi-sliders";
  if (label.includes("door")) return "bi bi-door-closed";
  if (label.includes("transfer") && label.includes("management")) return "bi bi-arrow-left-right";
  if (label.includes("shipment")) return "bi bi-box-seam";
  if (label.includes("customer") && label.includes("management")) return CUSTOMER_MENU_ICON;
  if (label.includes("supplier") && label.includes("management")) return SUPPLIER_MENU_ICON;
  return "";
};

const normalizeMenuTitle = (item) =>
  String(item?.title || "").trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");

const isPartnerManagementLeaf = (item) =>
  item?.content === "PartnerManagement" || normalizeMenuTitle(item) === "partner management";

/** Depth-first search; returns `{ siblings, index }` of the first matching leaf. */
const findMenuLeaf = (items, predicate) => {
  if (!Array.isArray(items)) return null;
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item?.children?.length) {
      const nested = findMenuLeaf(item.children, predicate);
      if (nested) return nested;
    } else if (predicate(item)) {
      return { siblings: items, index: i };
    }
  }
  return null;
};

/** Menu leaves that Customer / Supplier Management need in the sidebar. */
const MANAGEMENT_MENU_LEAVES = [
  // {
  //   match: (item) => {
  //     const t = normalizeMenuTitle(item);
  //     return item?.content === "CustomerManagement" || (t.includes("customer") && t.includes("management"));
  //   },
  //   leaf: {
  //     _id: "customer-management",
  //     id: "customer-management",
  //     title: "Customer Management",
  //     path: "/customer-management",
  //     content: "CustomerManagement",
  //     icon: CUSTOMER_MENU_ICON,
  //     collapsed: false,
  //   },
  // },
  // {
  //   match: (item) => {
  //     const t = normalizeMenuTitle(item);
  //     return item?.content === "SupplierManagement" || (t.includes("supplier") && t.includes("management"));
  //   },
  //   leaf: {
  //     _id: "supplier-management",
  //     id: "supplier-management",
  //     title: "Supplier Management",
  //     path: "/supplier-management",
  //     content: "SupplierManagement",
  //     icon: SUPPLIER_MENU_ICON,
  //     collapsed: false,
  //   },
  // },
];

/**
 * Ensure Customer Management and Supplier Management exist in the API-driven menu.
 * - If a matching leaf already exists it is left as-is (only a missing `content` is filled in).
 * - Otherwise the leaf is inserted right after Partner Management (same parent) so it
 *   inherits the same expanded/collapsed group behaviour; falls back to the root level.
 * Returns a new array; the input is not mutated.
 */
export const ensureManagementMenus = (menus) => {
  if (!Array.isArray(menus)) return menus;

  const clone = (items) =>
    items.map((item) =>
      item?.children?.length ? { ...item, children: clone(item.children) } : { ...item }
    );
  const result = clone(menus);

  MANAGEMENT_MENU_LEAVES.forEach(({ match, leaf }) => {
    const existing = findMenuLeaf(result, match);
    if (existing) {
      const item = existing.siblings[existing.index];
      // Always open the shared management screen (dummy list + view/edit), even if
      // the API leaf pointed at a different content key.
      existing.siblings[existing.index] = {
        ...item,
        title: item.title || leaf.title,
        path: item.path || leaf.path,
        content: leaf.content,
        icon: item.icon || item.iconClass || leaf.icon,
      };
      return;
    }

    const anchor = findMenuLeaf(result, isPartnerManagementLeaf);
    if (anchor) {
      // Keep Customer before Supplier when both are injected after Partner Management.
      const isManagementLeaf = (item) => MANAGEMENT_MENU_LEAVES.some((m) => m.match(item));
      let insertAt = anchor.index + 1;
      while (insertAt < anchor.siblings.length && isManagementLeaf(anchor.siblings[insertAt])) {
        insertAt += 1;
      }
      anchor.siblings.splice(insertAt, 0, { ...leaf });
    } else {
      result.push({ ...leaf });
    }
  });

  return result;
};

export const buildParentMap = (items, parent = null, map = {}) => {
  for (const item of items) {
    map[item.title] = parent;
    if (item.children) {
      buildParentMap(item.children, item.title, map);
    }
  }
  return map;
};

const isDataSetupMenu = (item) => {
  const title = (item?.title || "").trim().toLowerCase();
  const path = (item?.path || "").trim().toLowerCase();
  return title === "data setup" || path === "/data-setup";
};

/** Display names that differ from the title stored on the menu record. */
const MENU_TITLE_OVERRIDES = {
  "sales order/sto": "Sales Order",
};

const renameMenuItem = (item) => {
  if (!item || typeof item !== "object") return item;
  const key = String(item.title || "").trim().toLowerCase().replace(/\s+/g, " ");
  const title = MENU_TITLE_OVERRIDES[key] || item.title;
  return {
    ...item,
    title,
    ...(Array.isArray(item.children) ? { children: item.children.map(renameMenuItem) } : {}),
  };
};

/** Apply display-name overrides (e.g. "Sales Order/STO" → "Sales Order") across the menu tree. */
export const renameMenuTitles = (menus) => {
  if (!Array.isArray(menus)) return menus;
  return menus.map(renameMenuItem);
};

const isRemovedInventoryLeaf = (item) => {
  const title = normalizeMenuTitle(item);
  const path = String(item?.path || "").trim().toLowerCase();
  return (
    item?.content === "ItemLocation" ||
    item?.content === "HoldInventory" ||
    item?.content === "LotAndItem" ||
    // Inventory Ledger stays in the app; hide it from the Inventory Management sidebar only.
    item?.content === "InventoryLedger" ||
    title === "item & location" ||
    title === "item and location" ||
    title === "hold inventory" ||
    title === "lot and item" ||
    title === "lot & item" ||
    title === "inventory ledger" ||
    path === "/item-location" ||
    path === "/hold-inventory" ||
    path === "/lot-and-item" ||
    path === "/inventory-ledger"
  );
};

/** These views live as tabs inside Inventory Visibility, not as sidebar items. */
export const removeInventoryDetailMenus = (menus) => {
  if (!Array.isArray(menus)) return menus;
  const prune = (items) =>
    items
      .filter((item) => !isRemovedInventoryLeaf(item))
      .map((item) =>
        item?.children?.length ? { ...item, children: prune(item.children) } : item
      );
  return prune(menus);
};

/** Keep Data Setup at the bottom of the root sidebar menu. */
export const sortMenusWithDataSetupLast = (menus) => {
  if (!Array.isArray(menus) || menus.length === 0) return menus;
  const dataSetupItems = menus.filter(isDataSetupMenu);
  const rest = menus.filter((item) => !isDataSetupMenu(item));
  if (!dataSetupItems.length) return menus;
  return [...rest, ...dataSetupItems];
};

/** First leaf menu item that opens the tenant Dashboard (content key used by TabWrapper). */
export const findDashboardMenuLeaf = (items) => {
  if (!items?.length) return null;
  for (const item of items) {
    if (item.children?.length) {
      const nested = findDashboardMenuLeaf(item.children);
      if (nested) return nested;
    }
    if (item.content === "Dashboard") {
      return {
        title: item.title,
        path: item.path,
        content: item.content,
        iconClass: normalizeBootstrapIconClass(item.iconClass ?? item.icon),
      };
    }
  }
  return null;
};
